/**
 * LessonDetail — one lesson, presented as the design's stepper (CONFIG.lesson =
 * "steps"): a section at a time with an outline rail. Advancing runs a quick
 * mini-quiz (up to 3 questions from the lesson's topics, instant feedback) before
 * moving on, then marks the lesson complete at the end. Ports renderLesson +
 * lessonStepper + miniQuiz. Answers flow through the progress context (stats +
 * missed set). Lesson body is our own trusted content.
 */
import { useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { CheckCircle2 } from 'lucide-react';
import { lessons, questions, tests } from '@/content/training';
import { useTrainingProgress } from '../TrainingProgressContext';
import { trainingPath } from '../trainingNav';
import { splitLesson } from '../lessonSections';
import { Card, Button } from './TrainingUI';

const LETTERS = ['A', 'B', 'C', 'D', 'E'];

function shuffle<T>(a: T[]): T[] {
  const out = a.slice();
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
}

interface QuizItem {
  q: (typeof questions)[number];
  order: number[];
}
interface QuizState {
  items: QuizItem[];
  k: number;
  chosen: number | null;
  right: number;
}

export function LessonDetail() {
  const { lessonId } = useParams();
  // Key on lessonId so navigating lesson→lesson remounts and resets step/quiz/
  // seenRef — sections differ in count, so a stale step index would render blank.
  return <LessonDetailInner key={lessonId ?? ''} lessonId={lessonId} />;
}

function LessonDetailInner({ lessonId }: { lessonId?: string }) {
  const navigate = useNavigate();
  const progress = useTrainingProgress();

  const lesson = lessons.find((l) => l.id === lessonId);
  const sections = useMemo(() => (lesson ? splitLesson(lesson.body) : []), [lesson]);
  const seenRef = useRef<Set<string>>(new Set());

  const [step, setStep] = useState(0);
  const [quiz, setQuiz] = useState<QuizState | null>(null);

  if (!lesson) {
    return (
      <div className="mx-auto max-w-3xl">
        <Card>
          <p className="text-sm text-muted-foreground">That lesson doesn&apos;t exist.</p>
          <Button variant="ghost" className="mt-4" onClick={() => navigate(trainingPath('learn'))}>
            ← All lessons
          </Button>
        </Card>
      </div>
    );
  }

  const isLast = step === sections.length - 1;
  const idx = lessons.indexOf(lesson);
  const nextLesson = idx < lessons.length - 1 ? lessons[idx + 1] : null;

  const goToStep = (s: number) => {
    setStep(s);
    setQuiz(null);
    progress.setLastLesson(lesson.id, s);
  };

  const advance = () => {
    if (!isLast) {
      goToStep(step + 1);
    } else {
      progress.markLessonDone(lesson.id);
      navigate(trainingPath('learn'));
    }
  };

  const startMiniQuiz = () => {
    const topics = lesson.topics || [];
    const pool = shuffle(
      questions.filter(
        (q) => q.test === lesson.test && topics.includes(q.topic) && !seenRef.current.has(q.id),
      ),
    ).slice(0, 3);
    if (pool.length === 0) {
      advance();
      return;
    }
    pool.forEach((q) => seenRef.current.add(q.id));
    setQuiz({
      items: pool.map((q) => ({ q, order: shuffle(q.choices.map((_, x) => x)) })),
      k: 0,
      chosen: null,
      right: 0,
    });
  };

  // ---- mini-quiz view ----
  if (quiz) {
    const done = quiz.k >= quiz.items.length;
    if (done) {
      return (
        <div className="mx-auto max-w-3xl">
          <Card>
            <div className="ls-kicker">Quick check</div>
            <p className="mt-2">
              You got {quiz.right} of {quiz.items.length} right.
              {quiz.right < quiz.items.length
                ? ' The ones you missed are saved in Missed Questions.'
                : ''}
            </p>
            <Button variant="primary" className="mt-4" onClick={advance}>
              {isLast ? 'Finish lesson ✓' : 'Continue →'}
            </Button>
          </Card>
        </div>
      );
    }

    const item = quiz.items[quiz.k];
    const answered = quiz.chosen !== null;
    return (
      <div className="mx-auto max-w-3xl">
        <Card>
          <div className="ls-kicker">Quick check · {quiz.k + 1} of {quiz.items.length}</div>
          <div className="q-text mt-2">{item.q.q}</div>
          <div>
            {item.order.map((ci, pos) => {
              const isCorrect = ci === item.q.answer;
              const isChosen = ci === quiz.chosen;
              let tone = '';
              if (answered) {
                if (isCorrect) tone = ' correct';
                else if (isChosen) tone = ' wrong';
                else tone = ' dim';
              }
              return (
                <button
                  key={ci}
                  data-testid="mq-choice"
                  disabled={answered}
                  onClick={() => {
                    progress.recordAnswer(item.q, ci);
                    setQuiz((qz) =>
                      qz ? { ...qz, chosen: ci, right: qz.right + (ci === item.q.answer ? 1 : 0) } : qz,
                    );
                  }}
                  className={`choice${tone}`}
                >
                  <span className="c-letter">{LETTERS[pos]}</span>
                  <span>{item.q.choices[ci]}</span>
                </button>
              );
            })}
          </div>
          {answered && (
            <div className={`explain show ${quiz.chosen === item.q.answer ? 'good' : 'bad'}`}>
              <div className="verdict">
                {quiz.chosen === item.q.answer ? 'Correct' : 'Not quite'}
              </div>
              {item.q.exp}
            </div>
          )}
          <div className="mt-4 flex items-center justify-between">
            <Button variant="ghost" className="small" onClick={advance}>
              Skip check
            </Button>
            {answered && (
              <Button
                variant="primary"
                onClick={() => setQuiz((qz) => (qz ? { ...qz, k: qz.k + 1, chosen: null } : qz))}
              >
                {quiz.k < quiz.items.length - 1 ? 'Next question →' : 'See result'}
              </Button>
            )}
          </div>
        </Card>
      </div>
    );
  }

  // ---- reading view ----
  const section = sections[step];
  const done = !!progress.lessonsDone[lesson.id];

  return (
    <div className="mx-auto max-w-3xl">
      <Button variant="ghost" className="small mb-3" onClick={() => navigate(trainingPath('learn'))}>
        ← All lessons
      </Button>
      <h2 className="page-title">{lesson.title}</h2>
      <p className="muted">
        {tests[lesson.test].name} · Lesson {lesson.num}
      </p>

      <div className="ls-stepper mt-6">
        {/* outline rail */}
        <nav className="ls-rail hidden md:flex">
          {sections.map((s, k) => {
            const state = k === step ? 'on' : k < step ? 'done' : 'todo';
            return (
              <button
                key={k}
                onClick={() => goToStep(k)}
                className={`ls-step${state === 'on' ? ' on' : state === 'done' ? ' done' : ''}`}
              >
                <span className="ls-dot">{state === 'done' ? '✓' : k + 1}</span>
                <span className="truncate">{s.title || 'Overview'}</span>
              </button>
            );
          })}
        </nav>

        <div>
          {/* mobile section select */}
          <select
            className="ls-railsel mb-3 md:hidden"
            value={step}
            onChange={(e) => goToStep(Number(e.target.value))}
          >
            {sections.map((s, k) => (
              <option key={k} value={k}>
                Section {k + 1} of {sections.length} · {s.title || 'Overview'}
              </option>
            ))}
          </select>

          <Card>
            <div className="ls-kicker">
              Section {step + 1} of {sections.length}
            </div>
            {section?.title && <h4 className="ls-pane-title">{section.title}</h4>}
            <div
              className="lesson-body mt-3"
              dangerouslySetInnerHTML={{ __html: section?.html ?? '' }}
            />
            <div className="mt-6 flex items-center justify-between">
              <Button variant="ghost" disabled={step === 0} onClick={() => goToStep(step - 1)}>
                ← Back
              </Button>
              <Button variant="primary" onClick={startMiniQuiz}>
                {isLast ? 'Finish lesson ✓' : 'Next section →'}
              </Button>
            </div>
          </Card>

          <div className="mt-4 flex flex-wrap items-center gap-3">
            <Button
              variant={done ? 'ghost' : 'primary'}
              onClick={() => progress.markLessonDone(lesson.id)}
            >
              {done ? (
                <>
                  <CheckCircle2 className="h-4 w-4" /> Marked complete
                </>
              ) : (
                'Mark lesson complete'
              )}
            </Button>
            {nextLesson && (
              <Button
                variant="ghost"
                onClick={() => {
                  progress.markLessonDone(lesson.id);
                  navigate(`${trainingPath('learn')}/${nextLesson.id}`);
                }}
              >
                Next lesson →
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
